import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth, formatApiError } from "../lib/auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Handles Emergent OAuth callback. Reads session_id from URL fragment,
// posts it to backend, then redirects into the dashboard (or to /signup?plan=...
// if user came from a paid signup flow).
export default function AuthCallback() {
  const navigate = useNavigate();
  const { googleSession } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    const planParam = new URLSearchParams(window.location.search).get("plan");

    if (!sessionId) {
      setError("Missing session id");
      setDetails({ hash, search: window.location.search, href: window.location.href });
      return;
    }

    (async () => {
      try {
        await googleSession(sessionId);
        window.history.replaceState(null, "", window.location.pathname);
        if (planParam) {
          navigate(`/signup?plan=${planParam}`, { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        setError(formatApiError(e, "Could not complete Google sign-in."));
        setDetails({
          status: e?.response?.status ?? null,
          statusText: e?.response?.statusText ?? null,
          backendDetail: e?.response?.data?.detail ?? null,
          axiosCode: e?.code ?? null,
          axiosMessage: e?.message ?? null,
          requestUrl: `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`,
          frontendOrigin: window.location.origin,
          sessionIdPrefix: sessionId.slice(0, 8) + "…",
        });
      }
    })();
  }, [googleSession, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-5" data-testid="auth-callback-page">
      <div className="relative bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-[0_20px_50px_-15px_rgba(15,23,42,0.12)] text-center">
        <div className="flex justify-center mb-6">
          <Logo size="lg" />
        </div>
        {!error ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/25 flex items-center justify-center mb-5">
              <Loader2 className="w-7 h-7 text-[#1D6AFF] animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Signing you in…</h1>
            <p className="text-sm text-slate-500">One moment while we finish your Google sign-in.</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 border border-red-200 flex items-center justify-center mb-5">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Sign-in failed</h1>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed" data-testid="auth-callback-error">{error}</p>

            {details && (
              <div className="mb-5 text-left">
                <button
                  onClick={() => setShowDetails((v) => !v)}
                  data-testid="auth-callback-details-toggle"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 font-semibold uppercase tracking-wider"
                >
                  {showDetails ? "Hide" : "Show"} technical details
                  {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {showDetails && (
                  <pre
                    data-testid="auth-callback-details"
                    className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded-xl p-3 overflow-x-auto text-slate-700 whitespace-pre-wrap break-all"
                  >
{JSON.stringify(details, null, 2)}
                  </pre>
                )}
              </div>
            )}

            <button
              onClick={() => navigate("/login")}
              data-testid="auth-callback-retry"
              className="bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-5 py-2.5"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
